'use client';

import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  ThumbsUp,
  ThumbsDown,
  Reply,
  MoreHorizontal,
  Flag,
  LinkIcon,
  CheckCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useComments } from '../../hooks/use-comments';
import { useAuthStore } from '../../stores';
import { useGetUserById } from '../../hooks/use-user';
import { useCheckUserLogin } from '../../hooks/use-user';

export function CommentItem({ comment, postId, isReply = false }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [mintDialogOpen, setMintDialogOpen] = useState(false);

  const { walletAddress, isAuthenticated } = useAuthStore();
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserById(walletAddress);
  const {
    data: isLoggedIn,
    isLoading: isLoadingLogin,
    isError,
  } = useCheckUserLogin();
  const { addComment } = useComments();

  // Format the date
  const formattedDate = comment.createdAt
    ? formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })
    : 'recently';

  // Handle reply submission
  const handleReplySubmit = async () => {
    if (!replyContent.trim()) return;

    try {
      await addComment().mutateAsync({
        postId,
        content: replyContent,
        userId: user?.id,
        parentId: comment.id,
      });

      setReplyContent('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  // Get user role or title
  const userRole = user?.role || 'Member';

  // Get post count
  const postCount = user?.postCount || Math.floor(Math.random() * 10000);

  return (
    <div
      className={`${isReply ? 'ml-12' : ''
        } border-b border-white/5 bg-black/40 backdrop-blur-sm shadow-sm px-6 py-6 rounded-2xl transition-all hover:bg-white/5`}
    >
      <div className="flex gap-4">
        <div className="flex flex-col items-center">
          <Avatar className="h-10 w-10 md:h-12 md:w-12 border border-white/10 ring-2 ring-transparent group-hover:ring-violet-500/30 transition-all">
            <AvatarImage
              src={user?.avatar || '/placeholder.svg'}
              alt={user?.username || 'User'}
            />
            <AvatarFallback className="bg-zinc-800 text-zinc-400">
              {user?.username?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col">
            <div className="flex items-start justify-between">
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm md:text-base text-white">
                    {user?.username || 'Anonymous User'}
                  </span>
                  <Badge
                    variant="secondary"
                    className="text-xs bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/5"
                  >
                    {userRole}
                  </Badge>

                  {/* Mint Information Badge */}
                  {comment.mintInformation && (
                    <Dialog
                      open={mintDialogOpen}
                      onOpenChange={setMintDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Badge
                          variant="outline"
                          className="cursor-pointer flex items-center gap-1 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            setMintDialogOpen(true);
                          }}
                        >
                          <CheckCircle className="h-3 w-3" />
                          <span className="hidden sm:inline">
                            Verified on DKG
                          </span>
                          <span className="sm:hidden">Verified</span>
                        </Badge>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md bg-zinc-900/95 backdrop-blur-xl border-white/10 text-white">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2 text-white">
                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                            Comment Verification
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                            <p className="text-sm text-emerald-300 font-medium mb-1">
                              This comment has been verified and minted on the
                              blockchain via the OriginTrail Decentralized
                              Knowledge Graph (DKG).
                            </p>
                            <p className="text-xs text-emerald-400/60">
                              Ensuring authenticity and immutable record
                            </p>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between py-2 border-b border-white/5">
                              <span className="text-sm font-medium text-zinc-400">
                                Network
                              </span>
                              <div className="text-right">
                                <div className="text-sm text-white font-medium">
                                  {comment.mintInformation.chainName}
                                </div>
                                <div className="text-xs text-zinc-500">
                                  {comment.mintInformation.chainFullName}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-between py-2 border-b border-white/5">
                              <span className="text-sm font-medium text-zinc-400">
                                Token ID
                              </span>
                              <span className="text-sm font-mono text-violet-300 bg-violet-500/10 px-2 py-0.5 rounded">
                                #{comment.mintInformation.token_id}
                              </span>
                            </div>

                            <div className="flex items-center justify-between py-2 border-b border-white/5">
                              <span className="text-sm font-medium text-zinc-400">
                                Chain ID
                              </span>
                              <span className="text-sm font-mono text-white">
                                {comment.mintInformation.chainId}
                              </span>
                            </div>

                            <div className="flex items-center justify-between py-2">
                              <span className="text-sm font-medium text-zinc-400">
                                Environment
                              </span>
                              <Badge
                                variant={
                                  comment.mintInformation.chainType ===
                                    'testnet'
                                    ? 'secondary'
                                    : 'default'
                                }
                                className="bg-white/10 text-zinc-300 hover:bg-white/20 border-0"
                              >
                                {comment.mintInformation.chainType}
                              </Badge>
                            </div>
                          </div>

                          {comment.ual && (
                            <div className="pt-3 border-t border-white/5">
                              <span className="text-sm font-medium text-zinc-400 block mb-2">
                                Universal Asset Locator
                              </span>
                              <code className="text-xs bg-black/50 p-3 rounded-lg block overflow-x-auto text-zinc-300 border border-white/5 break-all whitespace-pre-wrap font-mono">
                                {comment.ual}
                              </code>
                            </div>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>

                <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                  <span>{postCount} posts</span>
                  <span>•</span>
                  <span>{formattedDate}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  <LinkIcon className="h-4 w-4" />
                  <span className="sr-only">Copy link</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  <Flag className="h-4 w-4" />
                  <span className="sr-only">Report</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">More options</span>
                </Button>
              </div>
            </div>

            <div className="mt-4 text-sm text-zinc-300 leading-relaxed">
              <p className="break-words">{comment.content}</p>
            </div>

            <div className="mt-4 flex items-center gap-6 hidden">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1 text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg"
                onClick={() => setShowReplyForm(!showReplyForm)}
              >
                <Reply className="h-4 w-4" />
                <span>Reply</span>
              </Button>

              <div className="flex items-center gap-1 text-zinc-400">
                <ThumbsUp className="h-4 w-4" />
                <span>{comment.upvotes || 0}</span>
              </div>

              <div className="flex items-center gap-1 text-zinc-400">
                <ThumbsDown className="h-4 w-4" />
                <span>{comment.downvotes || 0}</span>
              </div>
            </div>

            {showReplyForm && (
              <div className="mt-4 transition-all duration-300 ease-in-out hidden">
                <Textarea
                  placeholder="Write a reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="min-h-[100px] bg-black/50 border-white/10 text-white placeholder:text-zinc-600 focus:border-violet-500 focus:ring-violet-500/20 rounded-xl resize-none"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReplyForm(false)}
                    className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleReplySubmit}
                    className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white shadow-lg shadow-violet-500/20 rounded-lg"
                  >
                    Post Reply
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
